import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart3, FolderOpen, LogOut, Menu, X, Inbox } from "lucide-react";

const BASE_NAV_ITEMS = [
  { to: "/app/dashboard", label: "Dashboard", icon: BarChart3 },
  { to: "/app/projects",  label: "Projects",  icon: FolderOpen },
];

const ROLE_NAV_ITEMS = [
  { to: "/app/leads", label: "Leads", icon: Inbox, roles: ["tenant_admin"] },
];

export function ClientLayout() {
  const { user, logout, impersonating } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const handleLogout = () => { logout(); navigate("/login"); };

  // Merge base nav + role-specific items
  const navItems = [
    ...BASE_NAV_ITEMS,
    ...ROLE_NAV_ITEMS.filter(item => item.roles.includes(user?.role)),
  ];

  // Banner adds height on top — account for safe-area on notched iPhones
  const bannerPad = impersonating
    ? { paddingTop: "calc(env(safe-area-inset-top, 0px) + 38px)" }
    : undefined;

  return (
    <div
      className="flex h-[100dvh] bg-background overflow-hidden"
      style={bannerPad}
    >
      {/* ── Mobile backdrop ─────────────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      {/*   Mobile: off-canvas drawer via transform                         */}
      {/*   Desktop: always visible flex column                             */}
      <aside
        className={[
          "fixed md:relative inset-y-0 left-0 z-50",
          "w-64 md:w-60 flex-shrink-0 flex flex-col",
          "bg-card border-r border-border",
          "transition-transform duration-200 ease-in-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        ].join(" ")}
      >
        {/* Logo / header */}
        <div className="h-14 md:h-16 flex items-center justify-between px-5 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 md:w-7 md:h-7 rounded-md bg-primary/20 border border-primary/30 flex items-center justify-center">
              <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-sm bg-primary" />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-sm tracking-wide text-foreground" style={{ fontFamily: "Manrope, sans-serif" }}>
                Visual Platform
              </span>
              <span className="text-[9px] text-muted-foreground/50 font-mono tracking-widest uppercase">
                Client Portal
              </span>
            </div>
          </div>
          {/* Close — mobile only */}
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            aria-label="Close menu"
            data-testid="mobile-sidebar-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              data-testid={`client-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-primary/10 text-primary border border-primary/20 font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                }`
              }
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div
          className="p-3 border-t border-border"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
        >
          <div
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 cursor-pointer group"
            onClick={handleLogout}
            data-testid="client-logout-btn"
          >
            <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-foreground flex-shrink-0">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate font-mono">{user?.role}</p>
            </div>
            <LogOut className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      </aside>

      {/* ── Content area ────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

        {/* Mobile-only top bar */}
        <header className="flex-shrink-0 flex items-center justify-between h-14 px-4 border-b border-border bg-card/50 md:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            aria-label="Open menu"
            data-testid="mobile-sidebar-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>

          <span className="font-semibold text-sm text-foreground" style={{ fontFamily: "Manrope, sans-serif" }}>
            Visual Platform
          </span>

          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-foreground select-none">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
        </header>

        {/* Page content */}
        <main
          className="flex-1 overflow-auto"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
